/**
 * FloatingFeedbackButton — lightweight bottom-right widget visible on
 * all authenticated non-admin pages. Single-tap opens the
 * BetaFeedbackModal via the central context.
 *
 * Non-intrusive design: 44x44 minimum tap target, fades to 80% opacity
 * when not hovered, hidden on admin & auth routes (wired via
 * route-matching in App.js to avoid clutter on internal tools).
 */

import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { useBetaFeedback } from '@/contexts/BetaFeedbackContext';
import { useAuth } from '@/contexts/AuthContext';

const SURFACE = 'floating_button';

// Routes where the floating widget should NOT appear. Keeps the widget
// out of the way on admin tooling and the auth funnel.
const HIDDEN_ROUTE_PREFIXES = [
  '/admin',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/auth/callback',
  '/onboarding',
];

const FloatingFeedbackButton = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { openFeedbackModal, trackEvent } = useBetaFeedback();

  const path = location.pathname || '';
  const hidden = !user || HIDDEN_ROUTE_PREFIXES.some((p) => path.startsWith(p));

  // Fire an impression event ONCE per route the button mounts on.
  useEffect(() => {
    if (!hidden) trackEvent('impression', SURFACE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, hidden]);

  if (hidden) return null;

  return (
    <button
      type="button"
      onClick={() => openFeedbackModal(SURFACE)}
      aria-label="Give feedback"
      className="fixed bottom-4 right-4 sm:bottom-5 sm:right-5 z-40 inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-4 py-2.5 shadow-lg shadow-emerald-600/30 hover:bg-emerald-700 hover:shadow-emerald-700/40 active:scale-95 transition-all text-sm font-medium opacity-90 hover:opacity-100"
      data-testid="floating-feedback-button"
    >
      <MessageSquare className="h-4 w-4" />
      <span className="hidden xs:inline sm:inline">Feedback</span>
    </button>
  );
};

export default FloatingFeedbackButton;
